import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

export default function CtaSection() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');
    setStatus('sending');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: formData,
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = serverMsg?.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMsg(serverMsg || '送信に失敗しました。時間を置いて再度お試しください。');
      }
    } catch {
      setStatus('error');
      setErrorMsg('送信に失敗しました。時間を置いて再度お試しください。');
    }
  };

  return (
    <section id="contact" className="relative py-20 md:py-28 overflow-hidden bg-background-200">
      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          {/* CTA Text */}
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground-950 mb-4">
              まずは気軽に相談してみませんか？
            </h2>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
              Web制作、デザイン、動画、事務作業の代行...どんな小さな悩みでも大丈夫。仙台のパートナーとして、一緒に解決策を探します。
            </p>
          </div>

          {/* Contact Info Bar */}
          <div className="bg-background-50 rounded-2xl p-5 md:p-6 border border-background-200 mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-center gap-4 sm:gap-8 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-accent-600" />
                </div>
                <span className="text-foreground-700">
                  TEL: <strong className="text-foreground-950">022-211-5488</strong>
                </span>
                <span className="text-foreground-500 text-xs">（平日 9:00〜18:00）</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-accent-600" />
                </div>
                <span className="text-foreground-700">
                  Mail: <strong className="text-foreground-950">verite@frise.support</strong>
                </span>
              </div>
              <span className="text-foreground-500 text-xs whitespace-nowrap">通常2営業日以内にご返信</span>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-background-50 rounded-2xl p-6 md:p-10 border border-background-300">
            <h3 className="font-heading font-bold text-xl text-foreground-950 mb-6 text-center">
              お問い合わせ
            </h3>

            {status === 'success' ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-accent-600" />
                </div>
                <p className="text-foreground-700 font-medium">お問い合わせありがとうございます。通常2営業日以内にご返信いたします。</p>
              </div>
            ) : (
              <form
                action="https://readdy.ai/api/form/d93p0dtmi650so75dt1g"
                method="POST"
                data-readdy-form="d93p0dtmi650so75dt1g"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-2">
                      お名前
                    </label>
                    <input
                      type="text"
                      name="name"
                      placeholder="山田 太郎"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-background-400 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-shadow"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-2">
                      会社名
                    </label>
                    <input
                      type="text"
                      name="company"
                      placeholder="株式会社〇〇"
                      className="w-full px-4 py-3 rounded-lg border border-background-400 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-shadow"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-2">
                      メールアドレス
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="your@email.com"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-background-400 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-shadow"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-2">
                      電話番号
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="022-000-0000"
                      className="w-full px-4 py-3 rounded-lg border border-background-400 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-shadow"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-2">
                    ご希望のサービス
                  </label>
                  <select
                    name="service"
                    className="w-full px-4 py-3 rounded-lg border border-background-400 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-shadow"
                  >
                    <option value="">選択してください</option>
                    <option value="web">Web制作</option>
                    <option value="design">デザイン・印刷</option>
                    <option value="video">動画制作</option>
                    <option value="bpo">BPO・事務サポート</option>
                    <option value="other">その他・相談</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-2">
                    お問い合わせ内容
                  </label>
                  <textarea
                    name="message"
                    placeholder="お困りのことやご希望をお聞かせください..."
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-lg border border-background-400 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none transition-shadow"
                  />
                  <p className="text-xs text-foreground-500 mt-1 text-right">500文字以内</p>
                </div>

                <div className="hp-field">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                {status === 'error' && (
                  <div className="p-4 bg-primary-100 rounded-lg">
                    <p className="text-sm text-primary-700">{errorMsg}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full py-4 bg-primary-500 text-background-50 font-semibold rounded-full hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {status === 'sending' ? '送信中...' : '送信する'}
                </button>

                <p className="text-center text-xs text-foreground-500">
                  送信により、<Link to="/privacy" className="text-primary-600 underline hover:text-primary-700 cursor-pointer">プライバシーポリシー</Link>に同意したものとみなします。初回相談は完全無料です。
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}